import { makeSelectCurrentSnapshot } from 'pages/SnapshotManagement/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { ImmutableSnapshot } from './snapshotManagementTypes';
import {
  type SnapshotLicenseUsageValue,
  toSnapshotLicenseUsage,
} from './toSnapshotLicenseUsage';

interface SelectorProps {
  snapshot: ImmutableSnapshot;
}

const selector = createStructuredSelector({
  snapshot: makeSelectCurrentSnapshot(),
});

/**
 * License usage of the currently selected snapshot.
 *
 * `undefined` covers everything the License overview page cannot render: no snapshot selected, the
 * snapshot list not loaded yet, and snapshots that do not use the cloud licensing model (the API
 * sends `licenseUsage: null` for those). The page is gated on the same condition, so the last case
 * is not reachable through the menu - see `useCurrentSnapshotCloudLicensing`, which stays a cheap
 * boolean because it is evaluated at the app root on every snapshot change.
 */
export const useCurrentSnapshotLicenseUsage = ():
  | SnapshotLicenseUsageValue
  | undefined => {
  const { snapshot } = useSelector<unknown, SelectorProps>(selector);
  const licenseUsage = snapshot?.get('licenseUsage');

  // `toJS()` walks the whole submap, so it runs only when the snapshot itself changes; the result is
  // referentially stable in between, which matters because it feeds chart props.
  return useMemo(
    () => toSnapshotLicenseUsage(licenseUsage?.toJS()),
    [licenseUsage],
  );
};
